/**
 * What plan this account is on, and how much of today's voice allowance is left —
 * the two facts the Voice section, the mic button and the paywall all read.
 *
 * Two sources, deliberately, because they are two different truths (Voice Plans
 * Contract §3): the BFF is the only source of the PLAN (`GET /account/plan` — what
 * RevenueCat told it), the voice service is the only source of USAGE
 * (`GET /voice/v1/usage` — what it has metered today against the token's `daily_s`).
 * Neither asks the other, and neither is proxied through the other.
 *
 * One module-level store, read with useSyncExternalStore, so the menu row, the mic
 * gate and the sheet agree without a provider: a 429 from a clip updates the same
 * numbers the menu shows. Nothing here polls — usage is asked for when a surface
 * that shows it mounts and after every clip; the countdown is a clock, not a fetch.
 *
 * FAIL OPEN on the read, FAIL CLOSED on the meter (BILLING.md §10): if neither
 * endpoint answers, the mic stays usable — the service refuses a clip it will not
 * meter, and that refusal is the one that counts.
 */
import { useSyncExternalStore } from 'react'
import { ensureVoiceToken } from '../voice/voiceToken'

export type PlanName = 'free' | 'plus' | 'ultra'

/**
 * Is this tier a PAID one — i.e. anything above free?
 *
 * Owner, 2026-09-17: 這一個上限的名稱不應該叫做 Plus, 我們其實可以把它叫成 Ultra …
 * 目前並不會對外公開 … 日後我們是需要公開讓使用者來 subscribe Ultra 的.
 *
 * ONE PLACE, because the last tier was enumerated in six and every one of them read
 * `=== 'plus'`. A name the web has not been taught is not merely unrendered there —
 * `parsePlan` turns it into `free`, so an Ultra account would have been shown a
 * 10-minute day. A third tier must not mean six more edits, and a fourth must not
 * mean seven.
 */
export const isPaidPlan = (p: PlanName | null | undefined): boolean =>
  p === 'plus' || p === 'ultra'

/** Seconds a tier gets when the server did not say — the server always says. */
const DEFAULT_DAILY_S: Record<PlanName, number> = { free: 600, plus: 3600, ultra: 86400 }

/** The wire's plan string, or `free` when it is one we do not know. */
function planOf(v: unknown): PlanName {
  return v === 'plus' || v === 'ultra' || v === 'free' ? v : 'free'
}
export type PlanStatus = 'none' | 'active' | 'grace' | 'expired'

/** `GET /account/plan` — contract §4B. */
export type Plan = {
  plan: PlanName
  status: PlanStatus
  /** Unix seconds; null when there is nothing to expire. */
  expiresAt: number | null
  renews: boolean
  store: 'app_store' | 'play_store' | 'web' | null
  /** Today's voice allowance in seconds — the number the token carries. */
  dailyS: number
  /**
   * Plus because the account is on the BFF's comp list, not because anything was
   * bought (owner, 2026-09-13: 白名單帳號不需要付費也能使用 Voice Plus).
   *
   * It matters on screen for one reason: there is no subscription behind it, so
   * there is nothing to manage — a "Manage" button here can only lead to a store
   * that has never heard of this account. An older BFF omits the field, which
   * reads as false: bought, which is what every plan used to be.
   */
  comped: boolean
}

/** `GET /voice/v1/usage` and the fields a 429 quota refusal carries — contract §4D. */
export type Usage = {
  plan: PlanName
  limitS: number
  usedS: number
  remainingS: number
  /** Unix seconds of the next 00:00 UTC. */
  resetsAt: number
}

export type Billing = {
  /** null until the first answer arrives (or when signed out / unreachable). */
  plan: Plan | null
  usage: Usage | null
}

const FREE: Plan = { plan: 'free', status: 'none', expiresAt: null, renews: false, store: null, dailyS: 600, comped: false }

let state: Billing = { plan: null, usage: null }
const listeners = new Set<() => void>()

function emit(next: Billing) {
  state = next
  for (const l of listeners) l()
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

export function useBilling(): Billing {
  return useSyncExternalStore(subscribe, () => state, () => state)
}

export function getBilling(): Billing {
  return state
}

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback)

function parsePlan(body: Record<string, unknown>): Plan {
  const plan: PlanName = planOf(body.plan)
  const status = body.status
  const store = body.store
  return {
    plan,
    status: status === 'active' || status === 'grace' || status === 'expired' ? status : 'none',
    expiresAt: typeof body.expires_at === 'number' ? body.expires_at : null,
    renews: body.renews === true,
    store: store === 'app_store' || store === 'play_store' || store === 'web' ? store : null,
    dailyS: num(body.daily_s, DEFAULT_DAILY_S[plan]),
    comped: body.comped === true,
  }
}

/** The usage shape, from `/usage` or from a 429 body (which lacks remaining_s). */
function parseUsage(body: Record<string, unknown>): Usage | null {
  const limit = num(body.limit_s, -1)
  const resets = num(body.resets_at, -1)
  if (limit < 0 || resets <= 0) return null
  const used = num(body.used_s)
  return {
    plan: planOf(body.plan),
    limitS: limit,
    usedS: used,
    remainingS: num(body.remaining_s, Math.max(0, limit - used)),
    resetsAt: resets,
  }
}

/** The plan from the BFF. null = signed out (401) or unreachable — never a guess. */
async function fetchPlan(): Promise<Plan | null> {
  try {
    const res = await fetch('/account/plan', { credentials: 'include', cache: 'no-store' })
    if (!res.ok) return null
    return parsePlan((await res.json()) as Record<string, unknown>)
  } catch {
    return null
  }
}

/** Today's meter from the voice service, on the same bearer a clip would carry. */
async function fetchUsage(): Promise<Usage | null> {
  const token = await ensureVoiceToken()
  if (!token) return null
  try {
    const res = await fetch('/voice/v1/usage', {
      credentials: 'omit',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return null
    return parseUsage((await res.json()) as Record<string, unknown>)
  } catch {
    return null
  }
}

/**
 * Ask both sources and publish what they said. A source that did not answer keeps
 * its last value rather than blanking the row — an unreachable service is not
 * "no plan", and the store must never downgrade anyone on a network blip.
 */
export async function refreshBilling(): Promise<Billing> {
  const [plan, usage] = await Promise.all([fetchPlan(), fetchUsage()])
  emit({ plan: plan ?? state.plan, usage: usage ?? state.usage })
  return state
}

/** A 429 quota refusal knows today's numbers; keep them so the gate closes at once. */
export function noteQuotaRefusal(body: Record<string, unknown>): Usage | null {
  const usage = parseUsage(body)
  if (usage) emit({ ...state, usage: { ...usage, remainingS: 0 } })
  return usage
}

/** A clip the service accepted: charge it locally until the next real read. */
export function noteClip(durationS: number): void {
  const u = state.usage
  if (!u || durationS <= 0) return
  emit({
    ...state,
    usage: { ...u, usedS: u.usedS + durationS, remainingS: Math.max(0, u.remainingS - durationS) },
  })
}

/**
 * Is today's allowance gone, as far as this app knows RIGHT NOW? True only while
 * the last meter reading said so AND its reset has not passed — a stale "empty" from
 * yesterday must not block anyone this morning. Unknown usage = not exhausted (the
 * service is the enforcer; see the head of the file).
 */
export function isExhausted(now = Date.now() / 1000): boolean {
  const u = state.usage
  return !!u && u.remainingS <= 0 && u.resetsAt > now
}

/** The plan name to reason about when the BFF has not answered: free, never plus. */
export function planName(): PlanName {
  return (state.plan ?? FREE).plan
}

/** "in 3 h 12 m" / "in 4 m" / "any moment now" — the countdown to the next UTC midnight. */
export function resetsIn(resetsAt: number, now = Date.now() / 1000): string {
  const left = Math.max(0, Math.round(resetsAt - now))
  if (left < 60) return 'any moment now'
  const h = Math.floor(left / 3600)
  const m = Math.floor((left % 3600) / 60)
  if (h === 0) return `in ${m} m`
  return m === 0 ? `in ${h} h` : `in ${h} h ${m} m`
}

/** Whole minutes, floored — a person is not told "9.7 minutes". */
export function minutesOf(seconds: number): number {
  return Math.max(0, Math.floor(seconds / 60))
}

export function __resetBillingForTest(): void {
  state = { plan: null, usage: null }
}
